import apiClient from "./axiosConfig";

// Best-effort frontend -> backend log proxy. Intentionally:
// - production-only by default (to avoid noise during local dev)
// - throttled/deduped (to avoid spamming the backend)
// - fire-and-forget (never throws to callers)

const DEFAULT_THROTTLE_WINDOW_MS = 30_000;
const DEFAULT_MAX_PER_WINDOW = 5;
const DEFAULT_DEDUPE_TTL_MS = 5 * 60_000;

let windowStartedAt = 0;
let sentInWindow = 0;
const dedupe = new Map(); // key -> lastSentAt

function now() {
  return Date.now();
}

function isProd() {
  return process.env.NODE_ENV === "production";
}

function shouldEnable() {
  // Allow explicit opt-in/out via env; default to prod-only.
  // Values: "true"/"false" (case-insensitive)
  const v = process.env.REACT_APP_ENABLE_FRONTEND_CLOUD_LOG_PROXY;
  if (typeof v === "string") {
    if (v.toLowerCase() === "true") return true;
    if (v.toLowerCase() === "false") return false;
  }
  return isProd();
}

function normalizeError(err) {
  if (!err) return { message: "Unknown error", name: undefined, stack: undefined };

  if (err instanceof Error) {
    return { message: err.message || "Error", name: err.name, stack: err.stack };
  }

  // axios sometimes gives non-Error objects
  if (typeof err === "object") {
    const message = err.message || err.error || "Error";
    const name = err.name;
    const stack = err.stack;
    return { message, name, stack };
  }

  if (typeof err === "string") return { message: err, name: undefined, stack: undefined };
  return { message: "Error", name: undefined, stack: undefined };
}

function shouldIgnoreMessage(message) {
  if (!message) return false;
  // Common noisy/non-actionable browser errors
  const ignore = [
    "ResizeObserver loop limit exceeded",
    "ResizeObserver loop completed with undelivered notifications",
    "Script error.", // cross-origin scripts without CORS give no details
  ];
  return ignore.some((s) => message.includes(s));
}

function stableKey(payload) {
  // Keep it small; this is only for client-side dedupe.
  const msg = payload?.message || "";
  const name = payload?.name || "";
  const stackHead = String(payload?.stack || "").slice(0, 500);
  const url = payload?.url || "";
  const source = payload?.source || "";
  return `${name}|${msg}|${url}|${source}|${stackHead}`;
}

function allowSend(key, opts) {
  const t = now();
  const throttleWindowMs = opts?.throttleWindowMs ?? DEFAULT_THROTTLE_WINDOW_MS;
  const maxPerWindow = opts?.maxPerWindow ?? DEFAULT_MAX_PER_WINDOW;
  const dedupeTtlMs = opts?.dedupeTtlMs ?? DEFAULT_DEDUPE_TTL_MS;

  // Reset throttle window
  if (!windowStartedAt || t - windowStartedAt > throttleWindowMs) {
    windowStartedAt = t;
    sentInWindow = 0;
  }
  if (sentInWindow >= maxPerWindow) return false;

  const lastSent = dedupe.get(key);
  if (lastSent && t - lastSent < dedupeTtlMs) return false;

  sentInWindow += 1;
  dedupe.set(key, t);

  // Cheap cleanup
  if (dedupe.size > 200) {
    for (const [k, ts] of dedupe.entries()) {
      if (t - ts > dedupeTtlMs) dedupe.delete(k);
    }
  }

  return true;
}

export async function logErrorToCloud(error, extra = {}, opts = {}) {
  try {
    if (!shouldEnable()) return;

    const norm = normalizeError(error);
    if (shouldIgnoreMessage(norm.message)) return;

    const payload = {
      message: norm.message,
      name: norm.name,
      stack: norm.stack,
      url: typeof window !== "undefined" ? window.location.href : undefined,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      time: new Date().toISOString(),
      severity: "ERROR",
      ...extra,
    };

    const key = stableKey(payload);
    if (!allowSend(key, opts)) return;

    // Use apiClient so it follows existing /api baseURL and auth conventions.
    // Never throw to the app if logging fails.
    const response = await apiClient.post("/log/", payload, { timeout: 5000 });
    
    // Debug: log to console in development
    if (process.env.NODE_ENV === "development" || process.env.REACT_APP_DEBUG_CLOUD_LOGGING === "true") {
      console.log("[cloudLogProxy] Sent error log to backend:", { message: norm.message, response: response?.status });
    }
  } catch (err) {
    // Debug: log failures in development
    if (process.env.NODE_ENV === "development" || process.env.REACT_APP_DEBUG_CLOUD_LOGGING === "true") {
      console.warn("[cloudLogProxy] Failed to send error log:", err.message);
    }
  }
}

/**
 * Log a general message (INFO/WARNING/DEBUG) to Cloud Logging via the backend proxy.
 * Use this for important events that aren't errors.
 * 
 * @param {string} message - The log message
 * @param {"INFO"|"WARNING"|"DEBUG"} severity - Log severity level
 * @param {object} extra - Additional fields to include
 * @param {object} opts - Throttle/dedupe options
 */
export async function logToCloud(message, severity = "INFO", extra = {}, opts = {}) {
  try {
    if (!shouldEnable()) return;
    if (!message) return;

    // Enforce "errors only" from the client: do not send INFO/WARNING/DEBUG to the backend/GCP.
    // Keep CRITICAL as an error-equivalent.
    const sev = String(severity || "").toUpperCase();
    if (sev !== "ERROR" && sev !== "CRITICAL") return;

    const payload = {
      message,
      severity: sev,
      url: typeof window !== "undefined" ? window.location.href : undefined,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      time: new Date().toISOString(),
      ...extra,
    };

    const key = stableKey(payload);
    if (!allowSend(key, opts)) return;

    const response = await apiClient.post("/log/", payload, { timeout: 5000 });
    
    // Debug: log to console in development
    if (process.env.NODE_ENV === "development" || process.env.REACT_APP_DEBUG_CLOUD_LOGGING === "true") {
      console.log("[cloudLogProxy] Sent log to backend:", { severity, message, response: response?.status });
    }
  } catch (err) {
    // Debug: log failures in development
    if (process.env.NODE_ENV === "development" || process.env.REACT_APP_DEBUG_CLOUD_LOGGING === "true") {
      console.warn("[cloudLogProxy] Failed to send log:", err.message);
    }
  }
}

/**
 * Test function to verify the cloud logging pipeline is working.
 * Call this from browser console: import('./tools/cloudLogProxy').then(m => m.testCloudLogging())
 */
export async function testCloudLogging() {
  const wasEnabled = shouldEnable();
  console.log("[cloudLogProxy] shouldEnable():", wasEnabled);
  console.log("[cloudLogProxy] NODE_ENV:", process.env.NODE_ENV);
  console.log("[cloudLogProxy] REACT_APP_ENABLE_FRONTEND_CLOUD_LOG_PROXY:", process.env.REACT_APP_ENABLE_FRONTEND_CLOUD_LOG_PROXY);
  
  if (!wasEnabled) {
    console.log("[cloudLogProxy] Logging is disabled. Set REACT_APP_ENABLE_FRONTEND_CLOUD_LOG_PROXY=true to enable.");
    return { success: false, reason: "disabled" };
  }

  try {
    const payload = {
      message: "Test log from frontend - cloudLogProxy.testCloudLogging()",
      severity: "ERROR",
      url: typeof window !== "undefined" ? window.location.href : undefined,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
      time: new Date().toISOString(),
      test: true,
    };
    
    const response = await apiClient.post("/log/", payload, { timeout: 5000 });
    console.log("[cloudLogProxy] Test log sent successfully:", response.data);
    return { success: true, response: response.data };
  } catch (err) {
    console.error("[cloudLogProxy] Test log failed:", err);
    return { success: false, error: err.message };
  }
}


